/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-03-11 11:51:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';

import OldMenu from 'src/components/containers/main-menu-old';
import MainMenuItem from './item';
import { Popover } from '@shopify/polaris';
import { getMenuLeft, getMenuRight } from 'src/helpers/menu-helper.js';
import { allowUsingNewSearchSettings } from 'src/helpers/search-settings-helper';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import { ComposedProps } from './index.type';
import MenuIcon from 'src/assets/images/common/menu.png';
import MenuCloseIcon from 'src/assets/images/common/menu-close.png';

//-----------------------------------------
/**
 * menuIdMetaMatchOldNav: to matching for old nav bar, format XXX___YYY
 * XXX: parent of old nav, YYY: id of child in parent menu
 * ex: menu-theme___menu-integration-shopify-integration
 * for case: new nav, parent key is menu-integration, it not match parent menu-them in old nav
 *  menuId="menu-integration-theme-setup"
 *  menuIdMetaMatchOldNav="menu-theme___menu-integration-shopify-integration"
 *
 * @param {*} props
 * @returns
 */
const MainMenu = (props: ComposedProps) => {
  const {
    i18n,
    tenantApp,
    menuId = '',
    menuIdMetaMatchOldNav,
    themeSetupV2 = false,
    isNewSearch,
    useBoltOn
  } = props;
  const [popoverActiveMenuMini, setPopoverActionMenuMini] = useState(false);
  const togglePopoverActive = useCallback(() => {
    setPopoverActionMenuMini(prevState => !prevState);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', () => setPopoverActionMenuMini(false));
  }, []);

  const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);

  if (!isNewSearch)
    return <OldMenu menuId={menuId} menuIdMetaMatchOldNav={menuIdMetaMatchOldNav} />;
  const menuLeft = getMenuLeft(i18n, {
    hideAISearch: !isNewSearch,
    themeSetupV2,
  });
  const menuRight = getMenuRight(i18n, {
    themeSetupV2,
    hasShopifyIntegration: isEnableShopifyIntegration,
    useBoltOn
  });

  const activatorMiniMenu = (
    <span className="action" onClick={togglePopoverActive}>
      <img
        src={popoverActiveMenuMini ? MenuCloseIcon : MenuIcon}
        width={20}
        height={20}
        alt="menu"
      />
    </span>
  );

  return (
    <React.Fragment>
      <div className="PFS_MainMenu_Wrapper">
        <div className="PFS_MainMenu">
          <div className="PFS_MainMenu--Mini">
            <div className="left">
              <Popover
                active={popoverActiveMenuMini}
                activator={activatorMiniMenu}
                onClose={() => {}}
                preferredAlignment="left">
                {menuLeft.map(menuItem => (
                  <MainMenuItem
                    key={menuItem.id}
                    menuId={menuId}
                    menuIdMetaMatchOldNav={menuIdMetaMatchOldNav}
                    data={menuItem}
                    position="left"
                    className="menu-mobile"
                  />
                ))}
              </Popover>
            </div>
            <div className="right">
              {menuRight.map(menuItem => (
                <MainMenuItem
                  key={menuItem.id}
                  menuId={menuId}
                  menuIdMetaMatchOldNav={menuIdMetaMatchOldNav}
                  data={menuItem}
                  position="right"
                />
              ))}
            </div>
          </div>
        </div>
        <div className="PFS_MainMenu">
          <div className="menu-features">
            {menuLeft.map(menuItem => (
              <MainMenuItem
                key={menuItem.id}
                menuId={menuId}
                menuIdMetaMatchOldNav={menuIdMetaMatchOldNav}
                data={menuItem}
                position="left"
              />
            ))}
          </div>
          <div className="menu-settings">
            {menuRight.map(menuItem => (
              <MainMenuItem
                key={menuItem.id}
                menuId={menuId}
                menuIdMetaMatchOldNav={menuIdMetaMatchOldNav}
                data={menuItem}
                position="right"
              />
            ))}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const tenantAppSettings = _.get(state.oauth, 'tenantApp.settings', {});
  const useBoltOn = _.get(state.oauth, 'subscription.useBoltOn', false);
  const themeSetupV2 = _.get(tenantApp, 'adminAppFeatures.themeSetupV2', false);
  const searchVersion = _.get(state.oauth, 'tenantApp.parseParams.searchVersion', '1.0');
  const isNewSearch = allowUsingNewSearchSettings(searchVersion);

  return {
    tenantApp,
    tenantAppSettings,
    themeSetupV2: themeSetupV2,
    useBoltOn,
    isNewSearch
  };
};

export default connect(mapStateToProps)(withI18n()(MainMenu));
